/**
 * Colored status/label badge used across tables, chips, and details views.
 */

import * as React from 'react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

/** Visual variant for {@link LabelBadge} (status / gender / outlined). */
export type BadgeVariant =
  | 'ghost'
  | 'outlined'
  | 'solid'
  | 'active'
  | 'disabled'
  | 'on-hold'
  | 'male'
  | 'female'
  | 'inscrit';

/** Badge + icon row used by status / folder / subject catalogs. */
export interface StatusBadgeConfig {
  label: string;
  icon: LucideIcon;
  badgeVariant: BadgeVariant;
  badgeColor?: string;
}

/** Props for {@link LabelBadge}. */
interface LabelBadgeProps {
  label: string;
  icon?: LucideIcon | React.ReactNode;
  variant: BadgeVariant;
  /** Required except for active / disabled / on-hold variants. */
  color?: string;
  size?: 'sm' | 'md';
  className?: string;
}

/** Colored label badge for status, gender, or outlined variants. */
export const LabelBadge: React.FC<LabelBadgeProps> = ({
  label,
  icon: Icon,
  variant,
  color,
  size = 'sm',
  className,
}) => {
  const paddingClass = size === 'md' ? 'px-3 py-1' : 'px-2 py-0.5';

  const baseClasses = cn(
    'inline-flex items-center justify-center gap-1',
    'whitespace-nowrap shrink-0',
    'rounded-md text-xs font-medium',
    'min-w-[135px]',
    'transition-all',
    'border',
    paddingClass,
  );

  /** Render a Lucide component or a pre-built element; skip when no icon is set. */
  const renderIcon = () => {
    if (!Icon) return null;

    if (React.isValidElement(Icon)) {
      return Icon;
    }

    const IconComponent = Icon as LucideIcon;
    return React.createElement(IconComponent, {
      className: 'w-3 h-3',
    });
  };

  const semanticColors: Record<
    'active' | 'disabled' | 'on-hold' | 'male' | 'female' | 'inscrit',
    string
  > = {
    active: 'rgb(var(--status-success))',
    disabled: 'rgb(var(--status-neutral))',
    'on-hold': 'rgb(var(--status-pending))',
    male: 'rgb(var(--gender-male))',
    female: 'rgb(var(--gender-female))',
    inscrit: 'rgb(var(--status-success))',
  };

  const effectiveColor =
    variant === 'active' ||
    variant === 'disabled' ||
    variant === 'on-hold' ||
    variant === 'male' ||
    variant === 'female' ||
    variant === 'inscrit'
      ? semanticColors[variant]
      : color;

  if (!effectiveColor) {
    console.warn('LabelBadge: color is required for non-semantic variants');
    return null;
  }

  let style: React.CSSProperties = {
    borderColor: effectiveColor,
  };

  if (variant === 'ghost') {
    style = {
      ...style,
      backgroundColor: `color-mix(in srgb, ${effectiveColor} 15%, transparent)`,
      color: effectiveColor,
    };
  } else if (
    variant === 'outlined' ||
    variant === 'active' ||
    variant === 'disabled' ||
    variant === 'on-hold' ||
    variant === 'male' ||
    variant === 'female' ||
    variant === 'inscrit'
  ) {
    style = {
      ...style,
      backgroundColor: 'hsl(var(--card))',
      color: effectiveColor,
    };
  } else if (variant === 'solid') {
    style = {
      ...style,
      backgroundColor: effectiveColor,
      color: 'white',
    };
  }

  return (
    <div className={cn(baseClasses, className)} style={style}>
      {renderIcon()}
      <span className="text-center">{label}</span>
    </div>
  );
};
